import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Mic, MicOff, Loader2, X, Check, ChevronDown, Pill, FileText, Stethoscope, TestTube, ClipboardList, StickyNote } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Silence detection configuration
const SILENCE_THRESHOLD = 0.01;
const SILENCE_DURATION = 2500; // 2.5 seconds of silence before auto-stop
const MIN_RECORDING_DURATION = 1000; // Minimum 1 second recording

const GlobalVoiceDictation = ({ 
  onPrescriptionParsed,
  onChiefComplaintsParsed,
  onDiagnosisParsed,
  onTestsParsed,
  onAssessmentParsed,
  onAdditionalNotesParsed,
  onVitalsParsed,
  patientContext,
  doctorId,
  disabled = false,
  previousSessionMedications = [], // Medications from previous follow-up session
  onReuseMedicationsRequested // Callback when doctor says "use same medication"
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStep, setProcessingStep] = useState('');
  const [selectedField, setSelectedField] = useState('all');
  const [parsedResult, setParsedResult] = useState(null);
  const [showFieldSelector, setShowFieldSelector] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  
  const mediaRecorderRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const streamRef = useRef(null);
  const audioChunksRef = useRef([]);
  const silenceTimerRef = useRef(null);
  const recordingStartTimeRef = useRef(null);
  const animationFrameRef = useRef(null);
  const isRecordingRef = useRef(false);

  const fieldOptions = [
    { value: 'all', label: 'Auto Detect', icon: FileText, color: 'bg-purple-100 text-purple-700' },
    { value: 'prescription', label: 'Prescription', icon: Pill, color: 'bg-blue-100 text-blue-700' },
    { value: 'chief_complaints', label: 'Chief Complaints', icon: ClipboardList, color: 'bg-orange-100 text-orange-700' },
    { value: 'diagnosis', label: 'Diagnosis', icon: Stethoscope, color: 'bg-green-100 text-green-700' },
    { value: 'tests', label: 'Tests', icon: TestTube, color: 'bg-yellow-100 text-yellow-700' },
    { value: 'assessment', label: 'Assessment', icon: FileText, color: 'bg-teal-100 text-teal-700' },
    { value: 'additional_notes', label: 'Additional Notes', icon: StickyNote, color: 'bg-gray-100 text-gray-700' },
  ];

  // Keep isRecordingRef in sync with isRecording state
  useEffect(() => {
    isRecordingRef.current = isRecording;
  }, [isRecording]);

  const stopAllAudio = useCallback(() => {
    if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') mediaRecorderRef.current.stop();
    if (streamRef.current) streamRef.current.getTracks().forEach(track => track.stop());
    if (audioContextRef.current) audioContextRef.current.close();
  }, []);

  useEffect(() => {
    return () => stopAllAudio();
  }, [stopAllAudio]);

  const processAudio = useCallback(async () => {
    setIsProcessing(true);
    setProcessingStep('Transcribing...');
    
    try {
      const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
      const reader = new FileReader();
      
      reader.onloadend = async () => {
        try {
          const base64Audio = reader.result.split(',')[1];
          
          // Step 1: Transcribe
          const transcribeResponse = await axios.post(`${API}/api/ai-clinical/transcribe`, {
            audio_base64: base64Audio,
            mime_type: 'audio/webm',
            language: null,
            translate_to_english: true
          });
          
          // Backend returns { text: "...", success: true }
          const transcript = transcribeResponse.data.text;
          
          if (!transcribeResponse.data.success || !transcript || transcript.trim() === '') {
            toast.error('Could not transcribe audio. Please try again.');
            setIsProcessing(false);
            return;
          }
          
          // Check for "use same medication" or "repeat same medication" commands
          const lowerTranscript = transcript.toLowerCase();
          const reuseMedPatterns = [
            /use\s+same\s+medication/i,
            /repeat\s+same\s+medication/i,
            /same\s+medication/i,
            /continue\s+same\s+medication/i,
            /repeat\s+the\s+medication/i,
            /use\s+previous\s+medication/i,
            /reuse\s+medication/i,
            /use\s+the\s+same/i,
            /same\s+prescription/i,
            /repeat\s+prescription/i
          ];
          
          const isReuseMedCommand = reuseMedPatterns.some(pattern => pattern.test(lowerTranscript));
          
          console.log('Voice Rx - Transcript:', transcript);
          console.log('Voice Rx - Is Reuse Command:', isReuseMedCommand);
          console.log('Voice Rx - Previous Meds Available:', previousSessionMedications?.length || 0);
          
          if (isReuseMedCommand) {
            if (previousSessionMedications && previousSessionMedications.length > 0) {
              // Check for exclusion patterns (except antibiotic, eliminate antibiotic, etc.)
              const excludePatterns = [
                /except\s+(\w+)/gi,
                /eliminate\s+(\w+)/gi,
                /remove\s+(\w+)/gi,
                /without\s+(\w+)/gi,
                /exclude\s+(\w+)/gi,
                /no\s+(\w+)/gi
              ];
            
              let excludeTerms = [];
            excludePatterns.forEach(pattern => {
              let match;
              while ((match = pattern.exec(lowerTranscript)) !== null) {
                excludeTerms.push(match[1].toLowerCase());
              }
            });
            
            // Common antibiotic keywords to check
            const antibioticKeywords = ['antibiotic', 'antibiotics', 'amoxicillin', 'azithromycin', 'cephalosporin', 'penicillin', 'ciprofloxacin', 'augmentin', 'cefixime'];
            
            // Filter medications
            let filteredMeds = [...previousSessionMedications];
            
            if (excludeTerms.length > 0) {
              filteredMeds = filteredMeds.filter(med => {
                const medNameLower = (med.name || '').toLowerCase();
                const treatmentForLower = (med.treatment_for || '').toLowerCase();
                const medTypeLower = (med.medication_type || '').toLowerCase();
                
                // Check if any exclude term matches
                for (const term of excludeTerms) {
                  // If term is "antibiotic", check against all antibiotic keywords
                  if (term === 'antibiotic' || term === 'antibiotics') {
                    for (const abKeyword of antibioticKeywords) {
                      if (medNameLower.includes(abKeyword) || treatmentForLower.includes(abKeyword) || treatmentForLower.includes('infection')) {
                        return false;
                      }
                    }
                  } else {
                    // Direct match on medication name or treatment
                    if (medNameLower.includes(term) || treatmentForLower.includes(term) || medTypeLower.includes(term)) {
                      return false;
                    }
                  }
                }
                return true;
              });
            }
            
            // Trigger the reuse medications callback with filtered medications
            if (onReuseMedicationsRequested) {
              const excludeMsg = excludeTerms.length > 0 ? ` (excluding: ${excludeTerms.join(', ')})` : '';
              toast.success(`Found ${filteredMeds.length} medication(s) from previous session${excludeMsg}`);
              onReuseMedicationsRequested(filteredMeds);
              setIsProcessing(false);
              setProcessingStep('');
              return;
            }
            } else {
              // Command detected but no previous medications available
              toast.warning('No previous session medications found to reuse. This feature works in follow-up sessions.');
            }
          }
          
          // Step 2: Parse dictation (normal flow)
          setProcessingStep('Parsing...');
          const parseResponse = await axios.post(`${API}/api/ai-clinical/parse-dictation`, {
            text: transcript,
            field_type: selectedField,
            context: patientContext,
            doctor_id: doctorId
          });
          
          const result = parseResponse.data;
          setParsedResult(result);
          setProcessingStep('');
          setIsProcessing(false);
          
          // Show success
          toast.success('Dictation parsed successfully!');
        } catch (innerError) {
          console.error('Error in transcription/parsing:', innerError);
          const errorMsg = innerError.response?.data?.detail || innerError.message || 'Unknown error';
          toast.error(`Failed: ${errorMsg}`);
          setIsProcessing(false);
          setProcessingStep('');
        }
      };
      
      reader.readAsDataURL(audioBlob);
      
    } catch (error) {
      console.error('Error processing audio:', error);
      toast.error('Failed to process dictation. Please try again.');
      setIsProcessing(false);
      setProcessingStep('');
    }
  }, [selectedField, patientContext, previousSessionMedications, onReuseMedicationsRequested]);

  const stopRecording = useCallback(() => {
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    setIsRecording(false);
  }, []);

  const monitorAudioLevelRef = useRef(null);

  useEffect(() => {
    monitorAudioLevelRef.current = () => {
      if (!analyserRef.current) return;
      const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
      analyserRef.current.getByteTimeDomainData(dataArray);
      let sum = 0;
      for (let i = 0; i < dataArray.length; i++) {
        const value = (dataArray[i] - 128) / 128;
        sum += value * value;
      }
      const rms = Math.sqrt(sum / dataArray.length);
      setAudioLevel(rms);
      const currentlySilent = rms < SILENCE_THRESHOLD;
      
      if (isRecordingRef.current) {
        if (currentlySilent) {
          if (!silenceTimerRef.current) {
            silenceTimerRef.current = setTimeout(() => {
              const recordingDuration = Date.now() - recordingStartTimeRef.current;
              if (recordingDuration >= MIN_RECORDING_DURATION && audioChunksRef.current.length > 0) {
                stopRecording();
              }
            }, SILENCE_DURATION);
          }
        } else {
          if (silenceTimerRef.current) {
            clearTimeout(silenceTimerRef.current);
            silenceTimerRef.current = null;
          }
        }
      }
      
      animationFrameRef.current = requestAnimationFrame(monitorAudioLevelRef.current);
    };
  }, [stopRecording]);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      const source = audioContextRef.current.createMediaStreamSource(stream);
      source.connect(analyserRef.current);
      analyserRef.current.fftSize = 256;
      
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        if (audioChunksRef.current.length > 0) {
          await processAudio();
        }
      };
      
      mediaRecorder.start(100);
      recordingStartTimeRef.current = Date.now();
      setIsRecording(true);
      if (monitorAudioLevelRef.current) {
        monitorAudioLevelRef.current();
      }
      
    } catch (error) {
      console.error('Error starting recording:', error);
      toast.error('Could not access microphone. Please check permissions.');
    }
  }, [processAudio]);

  const applyResults = () => {
    if (!parsedResult) return;
    
    // Apply medications
    if (parsedResult.medications && parsedResult.medications.length > 0 && onPrescriptionParsed) {
      onPrescriptionParsed(parsedResult.medications);
      toast.success(`${parsedResult.medications.length} medication(s) added`);
    }
    
    // Apply chief complaints
    if (parsedResult.chief_complaints_text && onChiefComplaintsParsed) {
      onChiefComplaintsParsed(parsedResult.chief_complaints_text);
    } else if (parsedResult.chief_complaints && onChiefComplaintsParsed) {
      onChiefComplaintsParsed(parsedResult.chief_complaints);
    }
    
    // Apply diagnosis
    if (parsedResult.diagnosis_text && onDiagnosisParsed) {
      onDiagnosisParsed(parsedResult.diagnosis_text);
    } else if (parsedResult.diagnosis && onDiagnosisParsed) {
      onDiagnosisParsed(parsedResult.diagnosis);
    }
    
    // Apply tests
    if (parsedResult.tests_text && onTestsParsed) {
      onTestsParsed(parsedResult.tests_text);
    } else if (parsedResult.tests && onTestsParsed) {
      onTestsParsed(parsedResult.tests);
    }
    
    // Apply assessment
    if (parsedResult.assessment_text && onAssessmentParsed) {
      onAssessmentParsed(parsedResult.assessment_text);
    } else if (parsedResult.assessment && onAssessmentParsed) {
      onAssessmentParsed(parsedResult.assessment);
    }
    
    // Apply additional notes
    if (parsedResult.additional_notes && onAdditionalNotesParsed) {
      onAdditionalNotesParsed(parsedResult.additional_notes);
    }
    
    // Apply vitals
    if (parsedResult.vitals && onVitalsParsed) {
      onVitalsParsed(parsedResult.vitals);
    }
    
    // Close and reset
    setParsedResult(null);
    setIsOpen(false);
  };

  const selectedFieldOption = fieldOptions.find(f => f.value === selectedField);

  if (disabled) return null;

  return (
    <div className="relative">
      {/* Main Dictation Button */}
      <Button
        type="button"
        variant={isOpen ? "default" : "outline"}
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 ${isOpen ? 'bg-purple-600 hover:bg-purple-700 text-white' : 'border-purple-300 text-purple-700 hover:bg-purple-50'}`}
      >
        <Mic className="w-4 h-4" />
        <span className="hidden sm:inline">Voice Rx</span>
      </Button>
      
      {/* Dictation Panel */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Mic className="w-5 h-5" />
                <span className="font-semibold">Voice Dictation</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setIsOpen(false); setParsedResult(null); }}
                className="text-white hover:bg-white/20 h-8 w-8 p-0"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-xs text-purple-200 mt-1">
              Supports English, Hindi, Telugu and 90+ languages
            </p>
          </div>
          
          {/* Field Selector */}
          <div className="p-3 border-b">
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowFieldSelector(!showFieldSelector)}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg border ${selectedFieldOption?.color || 'bg-gray-100'}`}
              >
                <div className="flex items-center gap-2">
                  {selectedFieldOption?.icon && <selectedFieldOption.icon className="w-4 h-4" />}
                  <span className="text-sm font-medium">{selectedFieldOption?.label}</span>
                </div>
                <ChevronDown className={`w-4 h-4 transition-transform ${showFieldSelector ? 'rotate-180' : ''}`} />
              </button>
              
              {showFieldSelector && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto">
                  {fieldOptions.map(option => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => {
                        setSelectedField(option.value);
                        setShowFieldSelector(false);
                      }}
                      className={`w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-50 text-left ${
                        selectedField === option.value ? 'bg-purple-50' : ''
                      }`}
                    >
                      <option.icon className="w-4 h-4 text-gray-600" />
                      <span className="text-sm">{option.label}</span>
                      {selectedField === option.value && (
                        <Check className="w-4 h-4 text-purple-600 ml-auto" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          {/* Recording Area */}
          <div className="p-4">
            {!parsedResult ? (
              <div className="text-center">
                {/* Recording Button */}
                <button
                  type="button"
                  onClick={isRecording ? stopRecording : startRecording}
                  disabled={isProcessing}
                  className={`w-20 h-20 rounded-full flex items-center justify-center transition-all mx-auto ${
                    isRecording 
                      ? 'bg-red-500 hover:bg-red-600 animate-pulse' 
                      : isProcessing 
                        ? 'bg-gray-300 cursor-not-allowed'
                        : 'bg-purple-600 hover:bg-purple-700'
                  }`}
                >
                  {isProcessing ? (
                    <Loader2 className="w-8 h-8 text-white animate-spin" />
                  ) : isRecording ? (
                    <MicOff className="w-8 h-8 text-white" />
                  ) : (
                    <Mic className="w-8 h-8 text-white" />
                  )}
                </button>
                
                {/* Status Text */}
                <p className="mt-3 text-sm text-gray-600">
                  {isProcessing ? processingStep : isRecording ? 'Listening... (stops on silence)' : 'Tap to start dictating'}
                </p>
                
                {/* Audio Level Indicator */}
                {isRecording && (
                  <div className="mt-3 flex justify-center gap-1">
                    {[...Array(10)].map((_, i) => (
                      <div
                        key={i}
                        className={`w-1.5 rounded-full transition-all ${
                          audioLevel * 50 > i ? 'bg-purple-500' : 'bg-gray-200'
                        }`}
                        style={{ height: `${10 + i * 3}px` }}
                      />
                    ))}
                  </div>
                )}
                
                {/* Example Phrases */}
                {!isRecording && !isProcessing && (
                  <div className="mt-4 text-xs text-gray-500">
                    <p className="font-medium mb-1">Example phrases:</p>
                    {selectedField === 'prescription' || selectedField === 'all' ? (
                      <p className="italic">Tab Dolo 650 mg twice daily after food for 5 days</p>
                    ) : selectedField === 'chief_complaints' ? (
                      <p className="italic">Fever for 3 days with body pain</p>
                    ) : selectedField === 'diagnosis' ? (
                      <p className="italic">Viral upper respiratory infection</p>
                    ) : selectedField === 'tests' ? (
                      <p className="italic">CBC, Blood sugar fasting, X-ray chest</p>
                    ) : (
                      <p className="italic">Patient is stable, vitals normal</p>
                    )}
                  </div>
                )}
              </div>
            ) : (
              /* Parsed Results - Same order as Session Form */
              <div className="space-y-2 max-h-80 overflow-y-auto">
                <div className="flex items-center justify-between sticky top-0 bg-white pb-1">
                  <span className="text-sm font-medium text-gray-700">Parsed Results</span>
                  <Badge variant="outline" className="text-xs">
                    {Math.round((parsedResult.confidence_score || 0.9) * 100)}% confidence
                  </Badge>
                </div>
                
                {/* 1. Vitals - First in form */}
                {parsedResult.vitals && (
                  Object.entries(parsedResult.vitals).some(([, v]) => v && v !== '') && (
                    <div className="bg-red-50 rounded-lg p-2">
                      <div className="flex items-center gap-2 mb-1">
                        <svg className="w-3 h-3 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                        </svg>
                        <span className="text-xs font-medium text-red-800">Vitals</span>
                      </div>
                      <div className="grid grid-cols-2 gap-1 text-xs">
                        {parsedResult.vitals.temperature && (
                          <div className="text-gray-700">Temp: {parsedResult.vitals.temperature}°F</div>
                        )}
                        {parsedResult.vitals.pulse && (
                          <div className="text-gray-700">Pulse: {parsedResult.vitals.pulse}</div>
                        )}
                        {parsedResult.vitals.respiratory_rate && (
                          <div className="text-gray-700">RR: {parsedResult.vitals.respiratory_rate}</div>
                        )}
                        {parsedResult.vitals.spo2 && (
                          <div className="text-gray-700">SpO2: {parsedResult.vitals.spo2}%</div>
                        )}
                        {parsedResult.vitals.blood_pressure && (
                          <div className="text-gray-700">BP: {parsedResult.vitals.blood_pressure}</div>
                        )}
                        {parsedResult.vitals.height_cm && (
                          <div className="text-gray-700">Ht: {parsedResult.vitals.height_cm} cm</div>
                        )}
                        {parsedResult.vitals.weight_kg && (
                          <div className="text-gray-700">Wt: {parsedResult.vitals.weight_kg} kg</div>
                        )}
                      </div>
                    </div>
                  )
                )}
                
                {/* 2. Chief Complaints */}
                {parsedResult.chief_complaints_text && (
                  <div className="bg-orange-50 rounded-lg p-2">
                    <div className="flex items-center gap-2 mb-1">
                      <ClipboardList className="w-3 h-3 text-orange-600" />
                      <span className="text-xs font-medium text-orange-800">Chief Complaints</span>
                    </div>
                    <p className="text-xs text-gray-700">{parsedResult.chief_complaints_text}</p>
                  </div>
                )}
                
                {/* 3. Assessment */}
                {parsedResult.assessment_text && (
                  <div className="bg-teal-50 rounded-lg p-2">
                    <div className="flex items-center gap-2 mb-1">
                      <FileText className="w-3 h-3 text-teal-600" />
                      <span className="text-xs font-medium text-teal-800">Assessment</span>
                    </div>
                    <p className="text-xs text-gray-700">{parsedResult.assessment_text}</p>
                  </div>
                )}
                
                {/* 4. Tests */}
                {parsedResult.tests_text && (
                  <div className="bg-yellow-50 rounded-lg p-2">
                    <div className="flex items-center gap-2 mb-1">
                      <TestTube className="w-3 h-3 text-yellow-600" />
                      <span className="text-xs font-medium text-yellow-800">Tests</span>
                    </div>
                    <p className="text-xs text-gray-700">{parsedResult.tests_text}</p>
                  </div>
                )}
                
                {/* 5. Diagnosis */}
                {parsedResult.diagnosis_text && (
                  <div className="bg-green-50 rounded-lg p-2">
                    <div className="flex items-center gap-2 mb-1">
                      <Stethoscope className="w-3 h-3 text-green-600" />
                      <span className="text-xs font-medium text-green-800">Diagnosis</span>
                    </div>
                    <p className="text-xs text-gray-700">{parsedResult.diagnosis_text}</p>
                  </div>
                )}
                
                {/* 6. Medications */}
                {parsedResult.medications && parsedResult.medications.length > 0 && (
                  <div className="bg-blue-50 rounded-lg p-2">
                    <div className="flex items-center gap-2 mb-1">
                      <Pill className="w-3 h-3 text-blue-600" />
                      <span className="text-xs font-medium text-blue-800">Medications ({parsedResult.medications.length})</span>
                    </div>
                    <div className="space-y-1">
                      {parsedResult.medications.map((med, i) => (
                        <div key={i} className="bg-white rounded p-1.5 text-xs">
                          <div className="flex items-center gap-1 flex-wrap">
                            <span className="font-medium text-gray-800">{med.name}</span>
                            {med.database_match && med.source === 'C' ? (
                              <span className="text-green-600 text-[10px] bg-green-50 px-1 rounded font-semibold">C</span>
                            ) : med.database_match && med.source === 'P' ? (
                              <span className="text-blue-600 text-[10px] bg-blue-50 px-1 rounded font-semibold">P</span>
                            ) : (
                              <span className="text-orange-500 text-[10px] bg-orange-50 px-1 rounded">Voice</span>
                            )}
                          </div>
                          {med.spoken_name && med.database_match && med.spoken_name.toLowerCase() !== med.name.toLowerCase().replace(/^(tab|cap|syp|inj)\s+/i, '') && (
                            <div className="text-[10px] text-gray-400 italic">
                              Voice: &quot;{med.spoken_name}&quot;
                            </div>
                          )}
                          <div className="text-gray-500">
                            {med.timing && <span>{med.timing}</span>}
                            {med.duration && <span> • {med.duration}</span>}
                            {med.time_of_day?.length > 0 && <span> • {med.time_of_day.join(', ')}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* 7. Additional Notes / Advice */}
                {parsedResult.additional_notes && (
                  <div className="bg-gray-50 rounded-lg p-2">
                    <div className="flex items-center gap-2 mb-1">
                      <StickyNote className="w-3 h-3 text-gray-600" />
                      <span className="text-xs font-medium text-gray-800">Additional Notes</span>
                    </div>
                    <p className="text-xs text-gray-700">{parsedResult.additional_notes}</p>
                  </div>
                )}
                
                {/* Show raw text if nothing was parsed */}
                {!parsedResult.medications?.length && 
                 !parsedResult.chief_complaints_text && 
                 !parsedResult.diagnosis_text && 
                 !parsedResult.tests_text &&
                 !parsedResult.assessment_text &&
                 !(parsedResult.vitals && Object.values(parsedResult.vitals).some(v => v && v !== '')) &&
                 parsedResult.raw_text && (
                  <div className="bg-gray-50 rounded-lg p-2">
                    <div className="flex items-center gap-2 mb-1">
                      <FileText className="w-3 h-3 text-gray-600" />
                      <span className="text-xs font-medium text-gray-800">Transcribed Text</span>
                    </div>
                    <p className="text-xs text-gray-700 italic">{parsedResult.raw_text}</p>
                  </div>
                )}
                
                {/* Action Buttons */}
                <div className="flex gap-2 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setParsedResult(null)}
                    className="flex-1"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Discard
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    onClick={applyResults}
                    className="flex-1 bg-purple-600 hover:bg-purple-700"
                  >
                    <Check className="w-4 h-4 mr-1" />
                    Apply
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalVoiceDictation;
